import type { AuthLoginResponseData } from '@kimuno/api-sdk';
import config from 'config';
import CookiesJS from 'js-cookie';

const DOMAIN = config.cookies.domain;
const AUTH_TOKEN_COOKIE = `${DOMAIN}_authTokenData`;
const REFRESH_TOKEN_COOKIE = `${DOMAIN}_authRefreshTokenData`;
const NEW_PIN_ONBOARDING_COMPONENT_COOKIE = `${DOMAIN}_newPinOnboardingComponentData`;
const NEW_UI_ONBOARDING_COMPONENT_COOKIE = `${DOMAIN}_newUIOnboardingComponentData`;
const IS_COOKIE_SECURE = window.location.protocol.startsWith('https');
const SAME_SITE = IS_COOKIE_SECURE ? ('none' as const) : undefined;
const DEFAULT_COOKIE_OPTIONS = {
	secure: IS_COOKIE_SECURE,
	sameSite: SAME_SITE,
};
const ESSENTIAL_COOKIES = [AUTH_TOKEN_COOKIE, REFRESH_TOKEN_COOKIE];

const cookiesJS = CookiesJS.withConverter<unknown>({
	read: (value) => {
		return decodeURIComponent(value);
	},
	write: (value) => {
		if (typeof value !== 'string') {
			return encodeURIComponent(JSON.stringify(value));
		}
		return encodeURIComponent(value);
	},
});

// js-cookie v3 dropped the built-in `.getJSON()` helper, which silently fell back
// to the raw string when it wasn't valid JSON (e.g. the plain-string refresh token).
const getJSON = (name: string): unknown => {
	const value = cookiesJS.get(name);
	if (typeof value !== 'string') {
		return undefined;
	}
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
};

// Cookies are client-readable/writable, so their parsed contents aren't guaranteed
// to match the expected shape at runtime — validate before trusting them.
const readStringField = (value: unknown, key: string): string | undefined => {
	if (typeof value !== 'object' || value === null) {
		return undefined;
	}
	const field = (value as Record<string, unknown>)[key];
	return typeof field === 'string' ? field : undefined;
};

const hasStringToken = (value: unknown): value is Pick<AuthLoginResponseData, 'token'> => {
	return readStringField(value, 'token') !== undefined;
};

interface Cookies {
	deleteAuthToken: () => void;
	getAuthToken: () => string | undefined;
	getRefreshToken: () => string | undefined;
	getNewPinOnboardingComponent: () => string;
	isAuthTokenValid: () => boolean;
	setAuthToken: (tokenData: AuthLoginResponseData) => void;
	setNewPinOnboardingComponent: (state: string) => void;
	setNewUIOnboardingComponent: (state: string) => void;
	getAllCookies: () => { [key: string]: string };
	deleteNonEssential: () => void;
}

const cookies: Cookies = {
	deleteAuthToken: () => {
		cookiesJS.remove(AUTH_TOKEN_COOKIE, { domain: DOMAIN });
		cookiesJS.remove(REFRESH_TOKEN_COOKIE, { domain: DOMAIN });
	},
	getAuthToken: () => {
		const tokenData = getJSON(AUTH_TOKEN_COOKIE);
		return hasStringToken(tokenData) ? tokenData.token : undefined;
	},
	getRefreshToken: () => {
		const value = getJSON(REFRESH_TOKEN_COOKIE);
		return typeof value === 'string' ? value : undefined;
	},
	getNewPinOnboardingComponent: () => {
		return readStringField(getJSON(NEW_PIN_ONBOARDING_COMPONENT_COOKIE), 'state') ?? 'hidden';
	},
	isAuthTokenValid: () => {
		return cookies.getAuthToken() ? true : false;
	},
	setAuthToken: (tokenData: AuthLoginResponseData) => {
		const { expiresIn, refreshToken, refreshTokenExpiresIn } = tokenData;
		const expiresInDays = expiresIn / 1000 / 60 / 60 / 24;
		const refreshTokenExpiresInDays = refreshTokenExpiresIn / 1000 / 60 / 60 / 24;
		const authCookieOptions = {
			...DEFAULT_COOKIE_OPTIONS,
			domain: DOMAIN,
			expires: expiresInDays,
		};
		const refreshCookieOptions = {
			...DEFAULT_COOKIE_OPTIONS,
			domain: DOMAIN,
			expires: refreshTokenExpiresInDays,
		};

		cookiesJS.set(AUTH_TOKEN_COOKIE, tokenData, authCookieOptions);
		cookiesJS.set(REFRESH_TOKEN_COOKIE, refreshToken, refreshCookieOptions);
	},
	setNewUIOnboardingComponent: (state) => {
		const cookieData = { state };
		const cookieOptions = {
			...DEFAULT_COOKIE_OPTIONS,
			domain: DOMAIN,
			expires: new Date('2024-12-31'),
		};
		cookiesJS.set(NEW_UI_ONBOARDING_COMPONENT_COOKIE, cookieData, cookieOptions);
	},
	setNewPinOnboardingComponent: (state) => {
		const cookieData = { state };
		const cookieOptions = {
			...DEFAULT_COOKIE_OPTIONS,
			domain: DOMAIN,
			expires: new Date('2024-12-31'),
		};
		cookiesJS.set(NEW_PIN_ONBOARDING_COMPONENT_COOKIE, cookieData, cookieOptions);
	},
	getAllCookies: () => {
		return cookiesJS.get();
	},
	deleteNonEssential: () => {
		const currentCookies = cookiesJS.get();

		Object.keys(currentCookies).forEach((cookieName) => {
			if (!ESSENTIAL_COOKIES.includes(cookieName)) {
				cookiesJS.remove(cookieName, { domain: DOMAIN });
			}
		});
	},
};

export default cookies;
